import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "components/ui/card";

import { forwardRef, useImperativeHandle, useState } from "react";
import { PasswordConfigurationOptions, PasswordInput } from './password-config';
import UserClassOptionSelect from './user-class-option-select';
import { generateNumericPasswordAccess } from '@/utils/password-generator';
import { UserResponsibleTypeEnum } from '@/services/dtos/enums/accessAutomation.enum';


export enum PasswordConfigurationTypeEnum {
  NONE = "NONE",
  AUTOMATIC = "AUTOMATIC",
  MANUAL = "MANUAL",
}
interface PasswordConfigurationSelectedType {
  type: PasswordConfigurationTypeEnum
  value: string
}

export interface ConfigRef {
  getConfigData: () => {
    setOperatorPasswordError: (arg0: string[]) => void;
    operatorPassword: PasswordConfigurationSelectedType;

    setUserPasswordError: (arg0: string[]) => void;
    userPassword: PasswordConfigurationSelectedType;

    userClassValue: UserResponsibleTypeEnum;
  };
}

export type UserAccessConfigCardRef = ConfigRef;

export const UserAccessConfigCard = forwardRef<ConfigRef, { isSection?: boolean; defaultClass?: UserResponsibleTypeEnum; allowedClasses?: UserResponsibleTypeEnum[] }>(
  ({ isSection = false, defaultClass, allowedClasses }, ref) => {
    const [userOperatorClassValue, setUserOperatorClassValue] = useState<UserResponsibleTypeEnum>(defaultClass || UserResponsibleTypeEnum.ACCESSOR);

    const [userPassword, setUserPassword] = useState<PasswordConfigurationSelectedType>({
      type: PasswordConfigurationTypeEnum.AUTOMATIC, value: ""
    });
    const [userPasswordError, setUserPasswordError] = useState<string[]>();

    const [operatorPassword, setOperatorPassword] = useState<PasswordConfigurationSelectedType>({
      type: PasswordConfigurationTypeEnum.AUTOMATIC, value: ""
    });
    const [operatorPasswordError, setOperatorPasswordError] = useState<string[]>();

    useImperativeHandle(ref, () => ({
      getConfigData: () => {
        const newUserPassword = userPassword.type === PasswordConfigurationTypeEnum.AUTOMATIC
          ? { ...userPassword, value: generateNumericPasswordAccess(5) }
          : userPassword;

        const newOperatorPassword = operatorPassword.type === PasswordConfigurationTypeEnum.AUTOMATIC
          ? { ...operatorPassword, value: generateNumericPasswordAccess(5) }
          : operatorPassword;

        return {
          setOperatorPasswordError: setOperatorPasswordError,
          operatorPassword: newOperatorPassword,

          setUserPasswordError: setUserPasswordError,
          userPassword: newUserPassword,

          userClassValue: userOperatorClassValue,
        };
      },
    }));

    const content = (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <PasswordConfigurationOptions
          title="Definição de Senhas de acesso de Usuário"
          isSubSection={false}
          passwordType={userPassword.type}
          onPasswordTypeChange={(passwordType: PasswordConfigurationTypeEnum) =>
            setUserPassword({ type: passwordType, value: "" })
          }
        />
        <div className="space-y-4">
          <PasswordInput
            value={userPassword.value}
            hidden={userPassword.type !== PasswordConfigurationTypeEnum.MANUAL}
            onChange={(e) =>
              setUserPassword({ ...userPassword, value: e.target.value })
            }
            numericOnly={true}
            maxLength={5}
            error={userPasswordError}
          />
        </div>
        <PasswordConfigurationOptions
          title="Definição de Senhas de acesso de Operador"
          isSubSection={false}
          passwordType={operatorPassword.type}
          onPasswordTypeChange={(passwordType: PasswordConfigurationTypeEnum) =>
            setOperatorPassword({ type: passwordType, value: "" })
          }
        />
        <div className="space-y-4">
          <PasswordInput
            value={operatorPassword.value}
            hidden={operatorPassword.type !== PasswordConfigurationTypeEnum.MANUAL}
            onChange={(e) =>
              setOperatorPassword({ ...operatorPassword, value: e.target.value })
            }
            numericOnly={true}
            maxLength={5}
            error={operatorPasswordError}
          />
        </div>
        <div className="flex justify-between items-start">
          <UserClassOptionSelect
            defaultClass={userOperatorClassValue}
            onChange={(e) => setUserOperatorClassValue(e.value)}
            allowedClasses={allowedClasses}
          />
        </div>
      </div>
    );

    if (isSection) {
      return (
        <section aria-labelledby="user-access-config-heading">
          <h3 id="user-access-config-heading" className="text-lg font-medium mb-4">Configurações de Acesso</h3>
          {content}
        </section>
      );
    }

    return (
      <Card>
        <CardHeader>
          <CardTitle>Configurações de Acesso</CardTitle>
          <CardDescription>
            Defina configurações de acesso personalizadas para o funcionário
          </CardDescription>
        </CardHeader>
        <CardContent>
          {content}
        </CardContent>
      </Card>
    );
  }
);

export const Config = forwardRef<ConfigRef>(
  (_, ref) => {

    const [userOperatorClassValue, setUserOperatorClassValue] = useState<UserResponsibleTypeEnum>(UserResponsibleTypeEnum.ACCESSOR);

    const [userPassword, setUserPassword] = useState<PasswordConfigurationSelectedType>({
      type: PasswordConfigurationTypeEnum.AUTOMATIC, value: ""
    });
    const [userPasswordError, setUserPasswordError] = useState<string[]>();

    const [operatorPassword, setOperatorPassword] = useState<PasswordConfigurationSelectedType>({
      type: PasswordConfigurationTypeEnum.AUTOMATIC, value: ""
    });
    const [operatorPasswordError, setOperatorPasswordError] = useState<string[]>();


    useImperativeHandle(ref, () => ({
      getConfigData: () => {
        const newUserPassword = userPassword.type === PasswordConfigurationTypeEnum.AUTOMATIC
          ? { ...userPassword, value: generateNumericPasswordAccess(5) }
          : userPassword;

        const newOperatorPassword = operatorPassword.type === PasswordConfigurationTypeEnum.AUTOMATIC
          ? { ...operatorPassword, value: generateNumericPasswordAccess(5) }
          : operatorPassword;

        return {
          setOperatorPasswordError: setOperatorPasswordError,
          operatorPassword: newOperatorPassword,

          setUserPasswordError: setUserPasswordError,
          userPassword: newUserPassword,

          userClassValue: userOperatorClassValue,
        };
      },
    }));

    return (
      <Card x-chunk="A card with a form to edit the product details">
        <CardHeader>
          <CardTitle>Configurações de Acesso</CardTitle>
          <CardDescription>
            Defina configurações de acesso personalizadas para o funcionário
          </CardDescription>
        </CardHeader>

        <form>
          <CardContent className="space-y-6">
            <section aria-labelledby="user-permissions-heading">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <PasswordConfigurationOptions
                  title="Definição de Senhas de acesso de Usuário"
                  isSubSection={false}
                  passwordType={userPassword.type}
                  onPasswordTypeChange={(passwordType: PasswordConfigurationTypeEnum) =>
                    setUserPassword({ type: passwordType, value: "" })
                  }
                />
                <div className="space-y-4">
                  <PasswordInput
                    value={userPassword.value}
                    hidden={userPassword.type !== PasswordConfigurationTypeEnum.MANUAL}
                    onChange={(e) =>
                      setUserPassword({ ...userPassword, value: e.target.value })
                    }
                    numericOnly={true}
                    maxLength={5}
                    error={userPasswordError}
                  />
                </div>
                <PasswordConfigurationOptions
                  title="Definição de Senhas de acesso de Operador"
                  isSubSection={false}
                  passwordType={operatorPassword.type}
                  onPasswordTypeChange={(passwordType: PasswordConfigurationTypeEnum) =>
                    setOperatorPassword({ type: passwordType, value: "" })
                  }
                />
                <div className="space-y-4">
                  <PasswordInput
                    value={operatorPassword.value}
                    hidden={operatorPassword.type !== PasswordConfigurationTypeEnum.MANUAL}
                    onChange={(e) =>
                      setOperatorPassword({ ...operatorPassword, value: e.target.value })
                    }
                    numericOnly={true}
                    maxLength={5}
                    error={operatorPasswordError}
                  />
                </div>
                <div className="flex justify-between items-start">
                  <UserClassOptionSelect
                    defaultClass={userOperatorClassValue}
                    onChange={(e) => setUserOperatorClassValue(e.value)}
                  />
                </div>
              </div>
            </section>
          </CardContent>
        </form>
      </Card>
    );
  }
);
